import { Component, ElementRef, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { Textarea } from 'primeng/textarea';
import { RouterModule } from '@angular/router';
import { BadgesComponent } from './badges/badges.component';
import { KeepaGraphComponent } from '../parts/keepa-graph/keepa-graph.component';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DateService } from 'src/app/services/date.service';
import { MessageService } from 'src/app/services/message.service';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import {
  UserAmazonNoticeProductsGQL,
  UpsertUserAmazonNotificationGQL,
  UserAmazonNoticeProductsQuery,
  RemoveUserAmazonNotificationGQL,
} from 'src/gql/generated';
import { gqlErrors } from 'src/app/utils/gql-errors';

@Component({
  selector: 'app-amazon-nofication',
  templateUrl: './amazon-notification.component.html',
  styleUrls: ['./amazon-notification.component.sass'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    PanelModule,
    TableModule,
    SelectModule,
    MultiSelectModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    RouterModule,
    DialogModule,
    Textarea,
    BadgesComponent,
    KeepaGraphComponent,
  ],
})
export class AmazonNoficationComponent {
  products: UserAmazonNoticeProductsQuery['userAmazonNoticeProducts'] = [];
  allProducts: UserAmazonNoticeProductsQuery['userAmazonNoticeProducts'] = [];

  // メモ編集用
  showMemoDialog = false;
  currentProduct: UserAmazonNoticeProductsQuery['userAmazonNoticeProducts'][0] | null = null;
  memoText = '';

  // 一括選択用
  selectedProducts: UserAmazonNoticeProductsQuery['userAmazonNoticeProducts'] = [];
  loading = false;

  dateOpts = [{ label: '全て', value: 'all' }];
  filterDate = 'all';
  // keyが下の方にもあるので注意
  noticeOpts = [
    { label: '全て', value: 'all' },
    { value: 'is_minus_40', label: '40%以上減' },
    { value: 'is_plus_30', label: '30%以上増' },
    { value: 'is_minus_seller', label: '出品者減' },
    { value: 'is_zero_seller', label: '出品者0' },
    { value: 'is_return_seller', label: '出品者復活' },
    { value: 'is_return_amazon', label: 'Amazon復活' },
    { value: 'is_zero_amazon', label: 'Amazon0' },
    { value: 'is_minus_40_amazon', label: 'Amazon40%以上減' },
    { value: 'is_plus_30_amazon', label: 'Amazon30%以上増' },
  ];
  filterNotice = ['all'];
  csvHeader = [
    'ASIN',
    'JAN',
    '商品名',
    'Amazon価格',
    'ランキング',
    '出品者数',
    '前月販売',
    '価格減少（40%以上）',
    '価格上昇（30%以上）',
    '出品者復活',
    '出品者なし',
    '出品者減少（50%以上/3人以上）',
    'Amazon本体切れ',
    'Amazon本体復活',
    'Amazon本体価格急落',
    'Amazon本体価格急騰',
  ];

  constructor(
    private elementRef: ElementRef,
    private router: Router,
    private routingService: RoutingService,
    private subtitleService: SubtitleService,
    private userService: UserService,
    private dateService: DateService,
    private messageService: MessageService,
    private gql: UserAmazonNoticeProductsGQL,
    private upsertUserAmazonNotificationGQL: UpsertUserAmazonNotificationGQL,
    private removeUserAmazonNotificationGQL: RemoveUserAmazonNotificationGQL,
  ) {
    this.routingService.loginRequired();
    this.userService.amazonNotifyRequired();
    this.subtitleService.setSubTitle('Amazon通知');
  }

  async ngOnInit() {
    await this.loadAmazonNotification();
    this.buildSelectOptions();
  }

  async loadAmazonNotification() {
    const resp = await firstValueFrom(this.gql.fetch());
    this.products = resp.data?.userAmazonNoticeProducts || [];
    this.allProducts = [...this.products];
  }

  buildSelectOptions() {
    const filteredDate = [];

    this.products.forEach((p) => {
      p.amazonNotifications.forEach((n) => {
        if (!filteredDate.includes(n.notifiedAt)) {
          filteredDate.push(n.notifiedAt);
        }
      });
    });
    const sorted = filteredDate.sort((a, b) => {
      return a > b ? -1 : 1;
    });
    sorted.forEach((d) => {
      this.dateOpts.push({
        label: d,
        value: d,
      });
    });
  }

  onChangeFilterSelect(e: any) {
    const isDateAll = this.filterDate === 'all';
    const isNoticeAll = this.filterNotice.includes('all');
    if (isDateAll && isNoticeAll) {
      this.products = [...this.allProducts];
      return;
    }

    this.products = this.allProducts.filter((p) => {
      return p.amazonNotifications.some((n) => {
        const filterByDate = isDateAll || n.notifiedAt === this.filterDate;
        const filterByNotice = isNoticeAll || this.filterNotice.includes(n.notificationType);
        return filterByDate && filterByNotice;
      });
    });
    this.products.forEach((p) => {
      p.amazonNotifications = p.amazonNotifications?.filter((n) => {
        const filterByDate = isDateAll || n.notifiedAt === this.filterDate;
        const filterByNotice = isNoticeAll || this.filterNotice.includes(n.notificationType);
        return filterByDate && filterByNotice;
      });
    });
  }

  /**
   * 
   "ASIN",
    "商品名",
    "過去価格",
    "現在価格",
    "過去出品者数",
    "現在出品者数",
    "価格減少（40%以上）",
    "価格上昇（30%以上）",
    "出品者復活",
    "出品者なし",
    "出品者減少（50%以上/3人以上）",
    "Amazon本体切れ",
    "Amazon本体復活",
    "Amazon本体価格急落",
    "Amazon本体価格急騰",
  ];
    { value: "is_minus_40", label: "40%以上減" },
    { value: "is_plus_30", label: "30%以上増" },
    { value: "is_minus_seller", label: "出品者減" },
    { value: "is_zero_seller", label: "出品者0" },
    { value: "is_return_seller", label: "出品者復活" },
    { value: "is_return_amazon", label: "Amazon復活" },
    { value: "is_zero_amazon", label: "Amazon0" },
    { value: "is_minus_40_amazon", label: "Amazon40%以上減" },
    { value: "is_plus_30_amazon", label: "Amazon30%以上増" },

   */
  downloadCsv(table) {
    const values = this.products.length > 0 ? this.products : this.allProducts;
    const resultsHeader = this.csvHeader.join('\t');
    const csv =
      resultsHeader +
      '\n' +
      values
        .map((tmp) => {
          const ap = tmp.amazonProduct;
          const ans = tmp.amazonNotifications;
          const isTarget = (type) => (ans.find((n) => n.notificationType === type) ? 'o' : '');
          return [
            ap.asin,
            tmp.product.jan,
            ap.name,
            ap.latestPrice,
            ap.latestRanking,
            ap.newSellerNum,
            ap.keepaProduct?.boughtPastMonth != null
              ? ap.keepaProduct.boughtPastMonth < 10
                ? '9以下'
                : ap.keepaProduct.boughtPastMonth
              : '',
            isTarget('is_minus_40'),
            isTarget('is_plus_30'),
            isTarget('is_minus_seller'),
            isTarget('is_zero_seller'),
            isTarget('is_return_seller'),
            isTarget('is_return_amazon'),
            isTarget('is_zero_amazon'),
            isTarget('is_minus_40_amazon'),
            isTarget('is_plus_30_amazon'),
          ].join('\t');
        })
        .join('\n');

    this.outputCsv(csv, 'amazon_nofitication.tsv');
  }

  navigateToRegister() {
    this.router.navigate(['/amazon-notification/register']);
  }

  navigateToList() {
    this.router.navigate(['/amazon-notification/list']);
  }

  openMemoDialog(product: UserAmazonNoticeProductsQuery['userAmazonNoticeProducts'][0]) {
    this.currentProduct = product;
    this.memoText = product.userAmazonNotification?.memo || '';
    this.showMemoDialog = true;
  }

  async saveMemo() {
    if (!this.currentProduct) return;

    try {
      const result = await firstValueFrom(
        this.upsertUserAmazonNotificationGQL.mutate({
          variables: {
            asin: this.currentProduct.amazonProduct.asin,
            memo: this.memoText || null,
          },
        }),
      );
      console.log('Memo save result:', result);

      // GraphQLエラーをチェック
      if (gqlErrors(result)?.length > 0) {
        const error = gqlErrors(result)[0];
        let errorMessage = error.message || 'メモの保存に失敗しました';

        if (error.extensions?.code === 'W0001') {
          errorMessage = 'バリデーションエラーが発生しました。入力内容を確認してください。';
        }

        console.error('GraphQL errors:', gqlErrors(result));
        return;
      }

      if (result.data?.upsertUserAmazonNotification === true) {
        // 成功時の処理 - 配列を更新して読み取り専用プロパティの問題を回避
        this.products = this.products.map((product) => {
          if (product.amazonProduct.asin === this.currentProduct?.amazonProduct.asin) {
            return {
              ...product,
              userAmazonNotification: product.userAmazonNotification
                ? {
                    ...product.userAmazonNotification,
                    memo: this.memoText,
                  }
                : {
                    id: 0, // 仮のID
                    user: {
                      id: 0, // 仮のID
                      name: this.userService.getName() || '',
                    },
                    memo: this.memoText,
                    createdAt: new Date().toISOString(),
                  },
            };
          }
          return product;
        });

        // allProductsも同様に更新
        this.allProducts = this.allProducts.map((product) => {
          if (product.amazonProduct.asin === this.currentProduct?.amazonProduct.asin) {
            return {
              ...product,
              userAmazonNotification: product.userAmazonNotification
                ? {
                    ...product.userAmazonNotification,
                    memo: this.memoText,
                  }
                : {
                    id: 0,
                    user: {
                      id: 0, // 仮のID
                      name: this.userService.getName() || '',
                    },
                    memo: this.memoText,
                    createdAt: new Date().toISOString(),
                  },
            };
          }
          return product;
        });

        this.closeMemoDialog();
      } else {
        console.log('Unexpected result data:', result.data);
      }
    } catch (error) {
      console.error('Error saving memo:', error);
    }
  }

  closeMemoDialog() {
    this.showMemoDialog = false;
    this.currentProduct = null;
    this.memoText = '';
  }

  formatDate(dateString: string): string {
    return this.dateService.formatDate(new Date(dateString), 'YYYY/MM/DD HH:mm');
  }

  // 単一削除（確認なし）
  async deleteProduct(product: UserAmazonNoticeProductsQuery['userAmazonNoticeProducts'][0]) {
    try {
      const result = await firstValueFrom(
        this.removeUserAmazonNotificationGQL.mutate({
          variables: {
            asin: product.amazonProduct.asin,
          },
        }),
      );

      if (gqlErrors(result)?.length > 0) {
        const error = gqlErrors(result)[0];
        let errorMessage = error.message || '削除に失敗しました';
        if (error.extensions?.code === 'W0001') {
          errorMessage = 'バリデーションエラーが発生しました。';
        }
        this.messageService.setFlashMessageWithClear('error', 'エラー', errorMessage);
        return;
      }

      if (result.data?.removeUserAmazonNotification === true) {
        this.products = this.products.filter((p) => p.amazonProduct.asin !== product.amazonProduct.asin);
        this.allProducts = this.allProducts.filter((p) => p.amazonProduct.asin !== product.amazonProduct.asin);
        this.selectedProducts = this.selectedProducts.filter(
          (p) => p.amazonProduct.asin !== product.amazonProduct.asin,
        );
        this.messageService.setFlashMessageWithClear('success', '成功', '削除しました');
      } else {
        this.messageService.setFlashMessageWithClear('error', 'エラー', '削除に失敗しました');
      }
    } catch (error) {
      this.messageService.setFlashMessageWithClear('error', 'エラー', '削除に失敗しました');
      console.error('Error deleting notification:', error);
    }
  }

  // 一括削除（逐次処理）
  async deleteSelected() {
    if (this.selectedProducts.length === 0) return;

    this.loading = true;
    let successCount = 0;
    let failCount = 0;
    const toDelete = [...this.selectedProducts];

    for (const product of toDelete) {
      try {
        const result = await firstValueFrom(
          this.removeUserAmazonNotificationGQL.mutate({
            variables: {
              asin: product.amazonProduct.asin,
            },
          }),
        );

        if (result.data?.removeUserAmazonNotification === true) {
          successCount++;
          this.products = this.products.filter((p) => p.amazonProduct.asin !== product.amazonProduct.asin);
          this.allProducts = this.allProducts.filter((p) => p.amazonProduct.asin !== product.amazonProduct.asin);
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
        console.error('Error deleting notification:', error);
      }
    }

    this.selectedProducts = [];
    this.loading = false;

    if (failCount === 0) {
      this.messageService.setFlashMessageWithClear('success', '成功', `${successCount}件削除しました`);
    } else if (successCount > 0) {
      this.messageService.setFlashMessageWithClear('warn', '警告', `${successCount}件削除、${failCount}件失敗`);
    } else {
      this.messageService.setFlashMessageWithClear('error', 'エラー', '削除に失敗しました');
    }
  }

  // -------------------------------- private

  private outputCsv(csv: string, filename: string) {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const elem = this.elementRef.nativeElement;
    const link = elem.querySelector('#csv-donwload') as HTMLAnchorElement;
    link.href = url;
    link.download = filename;
    link.click();
  }
}
